import React, { useState } from 'react'
import { Message } from './types'
import summaryIcon from 'images/call-summary.svg'
import CallSummaryModal from './call-summary-modal'

interface CallSummaryTopic {
  id: string
  header: string
  text: string
}

interface CallSummary {
  id: number
  header: string
  text?: string
  main_topics?: CallSummaryTopic[]
}

interface CallSummaryCardProps {
  message: Message
  showTimestamp: boolean
  formatTimestamp: (timestamp: string) => string
}

const CallSummaryCard: React.FC<CallSummaryCardProps> = ({
  message,
  showTimestamp,
  formatTimestamp,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false)
  const isVoicemail = message.call?.voicemail || false
  const isFromMe = message.is_from_me || false
  const hasTranscription =
    message.call?.recording_data?.transcribed ||
    message.call?.recording?.transcribed
  const paragraphs =
    message.call?.recording_data?.paragraphs ||
    message.call?.recording?.paragraphs ||
    []
  const callSummary = message.call?.summary || message.call?.recording?.summary
  const summary = callSummary as CallSummary

  // Calculate audio timing
  const callDuration = message.call?.duration || 0
  const audioUrl = message.call?.recording_url

  return (
    <div>
      {showTimestamp && (
        <div className="text-center mb-3">
          <small className="text-white px-2 py-1 rounded">
            {formatTimestamp(message.sent_at)}
          </small>
        </div>
      )}

      <div
        className="bg-white p-0 mx-auto mb-3"
        style={{
          width: '100%',
          maxWidth: 'min(654px, 95vw)',
          minHeight: '0',
          border: '4px solid transparent',
          borderRadius: '16px 16px 16px 16px',
          backgroundImage: isVoicemail
            ? 'linear-gradient(white, white), linear-gradient(90deg, #9c27b0, #e91e63)' // Purple to Pink for voicemails
            : 'linear-gradient(white, white), linear-gradient(90deg, #4caf50, #2196f3)', // Green to Blue for calls
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          opacity: 1,
          position: 'relative',
          padding: '24px',
        }}
      >
        <div className="d-flex align-items-start m-3">
          <div
            style={{
              width: 'clamp(15px, 7vw, 20px)',
              height: 'clamp(15px, 7vw, 20px)',
              flexShrink: 0,
            }}
          >
            <img
              src={summaryIcon}
              alt="Summary Icon"
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <div
            style={{
              marginLeft: '10px',
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '10px',
                width: '85%',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: isFromMe ? '#1A202C' : '#000000',
              }}
            >
              {isVoicemail ? (
                <>
                  <ion-icon
                    name="mic"
                    className="me-2"
                    style={{ color: '#e91e63' }}
                  />
                  Voicemail:{' '}
                  {summary?.header ? (
                    summary.header
                  ) : (
                    <span>No summary available</span>
                  )}
                </>
              ) : (
                <>
                  Summary:{' '}
                  {summary?.header ? (
                    summary.header
                  ) : (
                    <span>No summary available</span>
                  )}
                </>
              )}
            </div>
          </div>
          <span style={{ flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center no-focus-shadow"
              onClick={() => setShowDetailModal(true)}
              style={{
                fontSize: 'clamp(12px, 3.5vw, 14px)',
                textDecoration: 'none',
                color: '#4ea6ff',
                fontWeight: 500,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 'clamp(6px, 2vw, 8px) clamp(10px, 3vw, 12px)',
                borderRadius: '100px',
                boxShadow: 'none',
              }}
            >
              Open Summary
              <ion-icon
                name="chevron-forward"
                style={{
                  marginLeft: 'clamp(2px, 1vw, 2px)',
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                }}
              />
            </button>
          </span>
        </div>

        {Array.isArray(summary?.main_topics) &&
          summary?.main_topics?.length > 0 && (
            <div className="ps-3 pb-3">
              <div
                className="mb-0 fw-bold"
                style={{ fontSize: '14px', color: '#1A202C' }}
              >
                Main Topics Discussed:
              </div>
              <ul
                className="mb-0 ps-4"
                style={{
                  listStyleType: 'disc',
                  fontSize: '14px',
                  color: '#1A202C',
                  lineHeight: '1.4',
                }}
              >
                {summary?.main_topics?.map((topic: CallSummaryTopic) => (
                  <li key={topic.id} className="mb-1">
                    <span>{topic.header}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div>

      <CallSummaryModal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        message={message}
        summary={summary}
        paragraphs={paragraphs}
        hasTranscription={hasTranscription}
        isVoicemail={isVoicemail}
        callDuration={callDuration}
        audioUrl={audioUrl || undefined}
      />
    </div>
  )
}

export default CallSummaryCard
